import { Component, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Navigate, useNavigate } from "react-router-dom";
import { Gantt, Willow } from "@svar-ui/react-gantt";
import "@svar-ui/react-gantt/style.css";
import api, { isDriveEmployee } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useProject } from "@/context/ProjectContext";
import { useTranslation } from "react-i18next";

const toDate = (s) => (s ? new Date(`${s}T00:00:00`) : null);

// SVAR Gantt intermittently crashes on init/resize (StrictMode double-mount race) — auto-remount on crash
class GanttBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { crashed: false, retry: 0 };
  }
  static getDerivedStateFromError() {
    return { crashed: true };
  }
  componentDidCatch() {
    this._t = setTimeout(() => this.setState((s) => ({ crashed: false, retry: s.retry + 1 })), 200);
  }
  componentWillUnmount() {
    clearTimeout(this._t);
  }
  render() {
    if (this.state.crashed) return <p className="py-10 text-center text-sm text-slate-400">…</p>;
    return <div key={this.state.retry} style={{ height: "100%" }} data-testid="gantt-boundary">{this.props.children}</div>;
  }
}

export default function StudioGantt() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { selectedId, project } = useProject();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ["work-items", selectedId],
    queryFn: async () => (await api.get(`/projects/${selectedId}/work-items`)).data,
    enabled: !!selectedId,
  });

  const { tasks, undated } = useMemo(() => {
    const items = data?.items || [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const out = [];
    let skip = 0;
    for (const it of items) {
      const end = toDate(it.planned_date);
      const start = toDate(it.start_date) || end;
      if (!start || !end) { skip += 1; continue; }
      const done = (it.progress ?? 0) >= 100;
      const overdue = !done && end < today;
      out.push({
        id: it.id,
        text: it.title,
        company: it.company_name,
        start,
        end: new Date(end.getTime() + 86400000),
        progress: Math.round(it.progress ?? 0),
        type: "task",
        status: done ? "done" : overdue ? "overdue" : "active",
      });
    }
    return { tasks: out, undated: skip };
  }, [data]);

  // SVAR Gantt crashes (init width undefined) if mounted with cached data before layout — gate on container width
  const wrapRef = useRef(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    if (el.clientWidth > 0) { setReady(true); return undefined; }
    const ro = new ResizeObserver(() => { if (el.clientWidth > 0) setReady(true); });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (!isDriveEmployee(user)) return <Navigate to="/" replace />;

  const scales = [
    { unit: "month", step: 1, format: "%F %Y" },
    { unit: "day", step: 1, format: "%j" },
  ];

  return (
    <div className="space-y-4" data-testid="studio-gantt-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">{t("studio.ganttTitle")}</h1>
          <p className="mt-1 text-sm text-slate-500">{t("studio.ganttSub")} {project?.name ? `· ${project.name}` : ""}</p>
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-500" data-testid="gantt-legend">
          <span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-sm bg-sky-600" />{t("studio.legendActive")}</span>
          <span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-sm bg-emerald-600" />{t("studio.legendDone")}</span>
          <span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-sm bg-rose-600" />{t("studio.legendOverdue")}</span>
        </div>
      </div>

      {undated > 0 && (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="gantt-undated-note">
          {t("studio.ganttUndated", { count: undated })}
        </p>
      )}

      <div ref={wrapRef} className="gantt-wrap overflow-hidden rounded-lg border bg-white" style={{ height: "calc(100vh - 15rem)" }} data-testid="gantt-chart">
        {isLoading || !ready ? (
          <p className="py-10 text-center text-sm text-slate-400">{t("common.loading")}</p>
        ) : tasks.length === 0 ? (
          <p className="py-12 text-center text-sm text-slate-400" data-testid="gantt-empty">{t("studio.ganttEmpty")}</p>
        ) : (
          <GanttBoundary>
            <Willow>
              <Gantt
                tasks={tasks}
                links={[]}
                scales={scales}
                readonly
                taskTemplate={({ data }) => (
                  <div className={`oc-gbar oc-gbar-${data.status || "active"}`} style={{ cursor: "pointer" }} title={t("studio.ganttOpenItem")} onClick={() => navigate(`/progress?focus=${data.id}`)} data-testid={`gantt-bar-${data.id}`}>
                    <div className="oc-gbar-fill" style={{ width: `${data.progress || 0}%` }} />
                  </div>
                )}
                columns={[
                  { id: "text", header: t("studio.ganttColTask"), width: 240 },
                  { id: "company", header: t("studio.ganttColCompany"), width: 130 },
                ]}
              />
            </Willow>
          </GanttBoundary>
        )}
      </div>

      <style>{`
        .gantt-wrap .oc-gbar { position: relative; height: 100%; width: 100%; border-radius: 4px; overflow: hidden; }
        .gantt-wrap .oc-gbar-active { background: #3983eb; }
        .gantt-wrap .oc-gbar-done { background: #059669; }
        .gantt-wrap .oc-gbar-overdue { background: #e11d48; }
        .gantt-wrap .oc-gbar-fill { position: absolute; top: 0; bottom: 0; left: 0; background: rgba(0, 0, 0, 0.28); }
      `}</style>
    </div>
  );
}
